import Dialog from "ui/elements/dialog/Dialog";
import {i18n} from "ui/i18n";
import Form from "ui/elements/form/Form";
import Utils from "ui/helpers/Utils";

/**
 * @typedef {object} T_RepeatSetupData
 * @property {number} times
 * @property {boolean} isReflected
 */

/**
 * Repeat Frames Setup Window
 */
export default class RepeatSetup extends Dialog {
	/**
	 * Class Constructor
	 */
	constructor() {
		super({
			title: i18n('title.repeat-setup'),
			smallWidth: true,
			buttons: [
				{
					caption: i18n('buttons.apply'),
					role: 'apply',
					icon: '/images/icons.svg#icon-svg-check'
				},
				{
					caption: i18n('buttons.close'),
					role: 'close'
				}
			]
		});

		/**
		 * @type {Form}
		 * @private
		 */
		this._form = null;

		/**
		 * @type {?function(data:(T_RepeatSetupData|null))}
		 * @private
		 */
		this._onComplete = null;

		void this._buildForm();
	}

	/**
	 * Returns promise which will be resolved in any case with one of two values:
	 * 1. Object - if form is ok and submitted;
	 * 2. null - if form has been closed;
	 *
	 * @returns {Promise<T_RepeatSetupData|null>}
	 */
	getResultPromise() {
		return new Promise((resolve) => {
			this._onComplete = resolve;
		});
	}

	/**
	 * Handles click by button
	 *
	 * @override
	 * @param {string} role
	 * @returns {void}
	 * @protected
	 */
	_handleButton(role) {
		super._handleButton(role);
		if (role === 'apply') {
			let data = this._form.getValues();
			if (data !== null) {
				this._apply(data);
			}
		}
	}

	/**
	 * Applies form data and destroy Dialog Box if all ok
	 *
	 * @private
	 * @param {T_RepeatSetupData} data
	 * @returns {void}
	 */
	_apply(data) {
		if (this._onComplete) {
			this._onComplete(data);
			this._onComplete = null;
			this.destroy();
		}
	}

	/**
	 * Builds form
	 *
	 * @private
	 * @returns {Promise<void>}
	 */
	async _buildForm() {
		let el = document.createElement('div');
		this._form = new Form({
			parent: el,
			modifier: 'form_width-50',
			onApply: this._apply.bind(this),
			values: {times: 0, isReflected: false},
			fields: [
				{
					type: 'uint',
					name: 'times',
					caption: i18n('inputs.timeline.repeat-times'),
					required: true,
					min: 0,
					max: 60,
					maxlength: 2,
					validators: [
						(value) => {
							if (value < 0 || value > 60) {
								return i18n('errors.timeline.repeat-times');
							}

							return '';
						}
					]
				},
				{
					type: 'checkbox',
					name: 'isReflected',
					caption: i18n('inputs.timeline.reflect'),
				},
			]
		});
		this.setContent(el);
		// focus first input
		await Utils.wait(40);
		let inp = el.querySelector('input');
		inp.focus();
		inp.select();
	}

	/**
	 * Destroys the dialog box
	 *
	 * @returns {void}
	 */
	destroy() {
		if (this._onComplete !== null) {
			this._onComplete(null);
			this._onComplete = null;
		}
		super.destroy();
	}
}
